/**
 * ReactionButton.js
 * 이모지 리액션 버튼 컴포넌트
 * 업무 경험 공유 페이지에서 사용
 */

import React, { useState } from 'react';
import { IconButton, Box } from '@mui/material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

// 이모지 매핑 (텍스트 라벨 없이 이모지만)
const REACTION_TYPES = {
  LIKE: '👍',
  FIRE: '🔥',
  STRENGTH: '💪',
  ROCKET: '🚀',
  SPARKLE: '✨',
};

const ReactionButton = ({ receiverCode, receiverName, initialCounts = {}, myReactions = [], onReactionSuccess }) => {
  const { colors, isDark } = useCustomTheme();
  const [counts, setCounts] = useState(initialCounts || { LIKE: 0, FIRE: 0, STRENGTH: 0, ROCKET: 0, SPARKLE: 0 });
  const [myList, setMyList] = useState(new Set(myReactions || []));
  const [loading, setLoading] = useState({});

  const handleToggle = async (type) => {
    // 이미 로딩 중이면 무시
    if (loading[type]) return;

    const isActive = myList.has(type);
    
    // 낙관적 업데이트 (Optimistic Update)
    const newCounts = { ...counts };
    const newMyList = new Set(myList);
    const newLoading = { ...loading, [type]: true };
    
    setLoading(newLoading);

    if (isActive) {
      newCounts[type] = Math.max(0, (newCounts[type] || 0) - 1);
      newMyList.delete(type);
    } else {
      newCounts[type] = (newCounts[type] || 0) + 1;
      newMyList.add(type);
    }

    setCounts(newCounts);
    setMyList(newMyList);

    try {
      console.log('[ReactionButton] 리액션 요청:', {
        receiver_code: receiverCode,
        receiver_name: receiverName,
        reaction_type: type,
        emoji: REACTION_TYPES[type]
      });
      
      const response = await axios.post(`${API_BASE_URL}/networking/reaction`, {
        receiver_code: receiverCode,
        reaction_type: type
      });
      
      console.log('[ReactionButton] 리액션 응답:', {
        status: response.data?.status,
        reaction_type: response.data?.reaction_type,
        counts: response.data?.counts
      });
      
      // 서버 응답으로 최신 카운트 업데이트
      if (response.data && response.data.counts) {
        setCounts(response.data.counts);
      }
      
      // 성공 시 메시지 표시 (추가된 경우만)
      if (response.data && response.data.status === 'added' && onReactionSuccess && receiverName) {
        const emoji = REACTION_TYPES[type];
        onReactionSuccess(receiverName, emoji);
      }
    } catch (error) {
      // 상세 오류 로그
      const errorInfo = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.response?.data?.detail || error.message,
        receiver_code: receiverCode,
        receiver_name: receiverName,
        reaction_type: type,
        url: error.config?.url,
        method: error.config?.method
      };
      
      if (error.response?.status === 400) {
        // 400 오류 상세 분석
        const errorMessage = error.response?.data?.detail || '리액션을 보낼 수 없습니다.';
        
        if (errorMessage.includes('본인에게는')) {
          console.warn('[ReactionButton] 리액션 400 오류 - 본인에게 리액션 시도:', errorInfo);
        } else if (errorMessage.includes('유효하지 않은')) {
          console.warn('[ReactionButton] 리액션 400 오류 - 유효하지 않은 리액션 타입:', errorInfo);
        } else {
          console.warn('[ReactionButton] 리액션 400 오류 - 기타:', errorInfo);
        }
      } else if (error.response?.status === 401) {
        console.warn('[ReactionButton] 리액션 401 오류 - 인증 실패:', errorInfo);
      } else if (error.response?.status === 404) {
        console.warn('[ReactionButton] 리액션 404 오류 - API 엔드포인트 없음:', errorInfo);
      } else if (error.response?.status === 500) {
        console.error('[ReactionButton] 리액션 500 오류 - 서버 오류:', errorInfo);
      } else {
        console.error('[ReactionButton] 리액션 처리 실패:', errorInfo, error);
      }
      
      // 롤백
      setCounts(counts);
      setMyList(myList);
    } finally {
      setLoading({ ...loading, [type]: false });
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
      {Object.keys(REACTION_TYPES).map((type) => {
        const icon = REACTION_TYPES[type];
        const isActive = myList.has(type);
        const isLoading = loading[type];

        return (
          <IconButton
            key={type}
            size="small"
            onClick={() => handleToggle(type)}
            disabled={isLoading}
            sx={{
              width: 32,
              height: 32,
              minWidth: 32,
              padding: 0,
              border: `1px solid ${isActive ? colors.accent : colors.primaryBorder}`,
              backgroundColor: isActive 
                ? (isDark ? 'rgba(138, 124, 255, 0.16)' : 'rgba(138, 124, 255, 0.08)')
                : 'transparent',
              borderRadius: '50%',
              color: isActive ? colors.accent : colors.secondaryText,
              '&:hover': {
                backgroundColor: isActive
                  ? (isDark ? 'rgba(138, 124, 255, 0.22)' : 'rgba(138, 124, 255, 0.12)')
                  : colors.hoverBg,
                borderColor: colors.accent,
              },
              '&:disabled': {
                opacity: 0.5,
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
              }}
            >
              {icon}
            </Box>
          </IconButton>
        );
      })}
    </Box>
  );
};

export default ReactionButton;

